import { useState } from "react";
import { useCompositionStore } from "../../stores/compositionStore.js";
import { useHistoryStore } from "../../stores/historyStore.js";
import { regenerateSection } from "../../infra/api.js";
import { Badge } from "../../ui/Badge.js";
import { Card } from "../../ui/Card.js";
import { Button } from "../../ui/Button.js";
import { ContinuityControl } from "./ContinuityControl.js";
import { StemMixer } from "./StemMixer.js";
import { SectionVariations } from "./SectionVariations.js";
import { SectionGenreShiftControl } from "../song-editor/SectionGenreShiftControl.js";

interface TimelineProps {
  /**
   * `true` when the editor is in history time-travel mode (Phase 8): the
   * Timeline shows a materialized past state and every edit control
   * (Generate / Regenerate / Gerar variações / Promover) is disabled, because
   * editing while viewing the past would append events onto the present log.
   */
  travelMode?: boolean;
}

/** Allowed batch sizes for "Gerar variações" — product asks 3–4, default 3. */
const VARIATION_COUNTS = [2, 3, 4] as const;
const DEFAULT_VARIATION_COUNT = 3;

export function Timeline({ travelMode = false }: TimelineProps): JSX.Element {
  const liveSections = useCompositionStore((s) => s.audioState.sections);
  const travelSections = useHistoryStore((s) => s.travelState?.sections);
  // In travel mode the Timeline reflects the materialized PAST state; never the
  // live one. The store still holds the present untouched — this is read-only.
  const sections = travelMode && travelSections !== undefined ? travelSections : liveSections;
  const projectId = useCompositionStore((s) => s.projectId);
  const generationStatus = useCompositionStore((s) => s.generation.status);
  const generationError = useCompositionStore((s) => s.generation.error);
  const variationStatus = useCompositionStore((s) => s.variations.status);
  const hasRunningGeneration = useCompositionStore((s) =>
    Object.values(s.jobs).some((j) => j.kind === "generation" && j.status === "running"),
  );

  // Per-section batch size, before the user clicks "Gerar variações".
  const [variationCount, setVariationCount] = useState<Record<string, number>>({});

  // The /generate endpoint is NOT idempotent — block the button while the POST
  // is in flight AND while any generation job is still running, so a second
  // click cannot enqueue a duplicate batch of jobs.
  const generationBusy = generationStatus === "requesting" || hasRunningGeneration;

  function onRegenerate(sectionId: string): void {
    const pid = useCompositionStore.getState().projectId;
    if (!pid) return;
    // First-class endpoint: server validates the section against materialized
    // state and the worker rebuilds the prompt, so no `prompt` is sent.
    void regenerateSection(pid, sectionId);
  }

  function onGenerate(): void {
    void useCompositionStore
      .getState()
      .generateSong()
      .catch(() => {
        // store has recorded the error in `generation.error`
      });
  }

  function onGenerateVariations(sectionId: string): void {
    const count = variationCount[sectionId] ?? DEFAULT_VARIATION_COUNT;
    void useCompositionStore
      .getState()
      .generateVariations(sectionId, count)
      .catch(() => {
        // store has recorded the error in `variations.error[sectionId]`
      });
  }

  if (sections.length === 0) {
    return <p className="text-ink-muted">No sections yet. Use the command bar to create some.</p>;
  }

  // Render by Section.order, not array index — robust to a future
  // SectionReordered event that could leave the array out of order.
  const ordered = [...sections].sort((a, b) => a.order - b.order);

  return (
    <section className="flex flex-col gap-3" aria-label="Composition timeline">
      <header className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
          {ordered.length} {ordered.length === 1 ? "seção" : "seções"}
        </span>
        <div className="flex items-center gap-3">
          {generationStatus === "error" && generationError ? (
            <span role="alert" className="text-xs text-accent-danger">
              {generationError}
            </span>
          ) : null}
          {generationBusy ? (
            <span className="font-mono text-[10px] uppercase tracking-widest2 text-butter">
              gerando…
            </span>
          ) : null}
          <Button
            variant="primary"
            disabled={!projectId || generationBusy || travelMode}
            onClick={onGenerate}
            aria-label="Generate song"
          >
            {generationStatus === "requesting"
              ? "Enfileirando…"
              : hasRunningGeneration
                ? "Gerando…"
                : "Generate"}
          </Button>
        </div>
      </header>

      <ol className="flex flex-col gap-2" aria-label="Section timeline">
        {ordered.map((section) => {
          const bound = section.audio !== null && section.audio !== undefined;
          const status: "bound" | "generating" | "empty" = bound
            ? "bound"
            : hasRunningGeneration
              ? "generating"
              : "empty";
          const statusTone: "neutral" | "active" =
            status === "bound" || status === "generating" ? "active" : "neutral";
          const count = variationCount[section.id] ?? DEFAULT_VARIATION_COUNT;
          const variationBusy = variationStatus[section.id] === "requesting";
          return (
            <li key={section.id}>
              <Card className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 shrink-0 font-mono text-[10px] tabular-nums text-ink-dim">
                    {String(section.order + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{section.name}</span>
                      <Badge tone="neutral" aria-label={`Role: ${section.role}`}>
                        {section.role}
                      </Badge>
                      {section.genreShift ? (
                        <span
                          className="inline-flex items-center gap-1 border border-moss px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2 text-moss"
                          aria-label={`Genre-shift: ${section.genreShift.descriptor}`}
                        >
                          <span aria-hidden="true">↻</span>
                          {section.genreShift.descriptor}
                        </span>
                      ) : null}
                    </div>
                    {section.prompt ? (
                      <div className="truncate text-xs text-ink-muted">{section.prompt}</div>
                    ) : null}
                  </div>
                  <Badge tone={statusTone}>{status}</Badge>
                  <div className="flex items-center gap-3">
                    <ContinuityControl />
                    <Button
                      variant="secondary"
                      disabled={
                        status === "generating" || !projectId || !section.audio || travelMode
                      }
                      onClick={() => {
                        onRegenerate(section.id);
                      }}
                    >
                      Regenerate
                    </Button>
                  </div>
                </div>

                <SectionGenreShiftControl section={section} disabled={travelMode} />

                <details className="group border-t border-border pt-3">
                  <summary className="flex cursor-pointer list-none items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest2 text-ink-muted hover:text-paper">
                    <span
                      aria-hidden="true"
                      className="inline-block transition-transform group-open:rotate-90"
                    >
                      ›
                    </span>
                    Variações (A/B)
                  </summary>
                  <div className="flex flex-col gap-3 pt-3">
                    <div className="flex items-center gap-2">
                      <label
                        className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest2 text-ink-muted"
                        htmlFor={`var-count-${section.id}`}
                      >
                        quantidade
                        <select
                          id={`var-count-${section.id}`}
                          className="border border-border bg-asphalt px-3 py-1.5 font-mono text-[11px] tabular-nums text-paper focus:border-paper focus:outline-none disabled:opacity-40"
                          value={count}
                          disabled={!projectId || variationBusy || travelMode}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            setVariationCount((prev) => ({ ...prev, [section.id]: next }));
                          }}
                          aria-label={`Quantidade de variações para ${section.name}`}
                        >
                          {VARIATION_COUNTS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                      <Button
                        variant="primary"
                        aria-label={`Gerar variações de ${section.name}`}
                        disabled={!projectId || variationBusy || travelMode}
                        onClick={() => {
                          onGenerateVariations(section.id);
                        }}
                      >
                        {variationBusy ? "Enfileirando…" : "Gerar variações"}
                      </Button>
                    </div>
                    <SectionVariations
                      sectionId={section.id}
                      readOnly={travelMode}
                      {...(travelMode ? { variationsOverride: section.variations } : {})}
                    />
                  </div>
                </details>

                <details className="group border-t border-border pt-3">
                  <summary className="flex cursor-pointer list-none items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest2 text-ink-muted hover:text-paper">
                    <span
                      aria-hidden="true"
                      className="inline-block transition-transform group-open:rotate-90"
                    >
                      ›
                    </span>
                    Mixer de stems
                  </summary>
                  <div className="pt-3">
                    <StemMixer sectionId={section.id} />
                  </div>
                </details>
              </Card>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
